import { FormInputDescriptor } from "@knowt/syncing/constants/ai-tools";
import { SetState } from "@knowt/syncing/types/common";
import {
    AIBooleanInput,
    AIFilesDnDInput,
    AINumberInput,
    AIToolsSelect,
    AIToolsTextRecordArea,
    TextLengthInput,
} from "../components/AIToolsInputs";
import { AICompletionType } from "@knowt/syncing/graphql/schema";
const TextLengthUnits = [
    { label: "Paragraph", value: "Paragraph" },
    { label: "Word", value: "Word" },
    { label: "Character", value: "Character" },
];

type FormFactoryProps = {
    tool: AICompletionType;
    inputs: FormInputDescriptor[];
    values: { [key: string]: any };
    droppedFile: File | null;
    setDroppedFile: SetState<File | null>;
    handleHelpFillField: (description: string, inputName: string) => Promise<string>;
    onChange: (name: string, value: any) => void;
    isUploadingFile: boolean;
    handleStopUloadFile: () => void;
    isShowingMissingInputs: boolean;
};

const AiFormInputFactory: React.FC<FormFactoryProps> = ({
    tool,
    inputs,
    values,
    droppedFile,
    setDroppedFile,
    handleHelpFillField,
    onChange,
    isUploadingFile,
    handleStopUloadFile,
    isShowingMissingInputs,
}) => {
    return inputs.map(input => {
        switch (input.type) {
            case "textInput":
                return (
                    <AIToolsTextRecordArea
                        id={`${tool}-${input.name}`}
                        key={`${input.name}-textInput`}
                        label={input.label}
                        placeholder={input.placeholder}
                        handWrittenValue={values[input.name]}
                        onTextChange={text => onChange(input.name, text)}
                        handleHelpFillField={desc => handleHelpFillField(desc, input.name)}
                        textAreaStyle={{
                            minHeight: "2rem",
                        }}
                        containerStyle={{
                            paddingBottom: 0,
                        }}
                        disabledAI
                        isMandatory={input.required}
                        isShowingMissingInputs={isShowingMissingInputs}
                    />
                );

            case "select":
                if (!input.options) return null;

                return (
                    <AIToolsSelect
                        id={`${tool}-${input.name}`}
                        key={`${input.name}-select`}
                        label={input.label}
                        options={input.options}
                        selected={
                            input.options.some(option => option.value === values[input.name]?.value) //for tools inputs keys conflicts
                                ? values[input.name]
                                : { label: input.placeholder, value: null }
                        }
                        onChange={value => onChange(input.name, value)}
                        isHorizontalLayout={input.layout?.horizontal}
                        isMandatory={input.required}
                        isShowingMissingInputs={isShowingMissingInputs}
                    />
                );

            case "textarea":
                return (
                    <AIToolsTextRecordArea
                        id={`${tool}-${input.name}`}
                        key={`${input.name}-textarea`}
                        label={input.label}
                        placeholder={input.placeholder}
                        handWrittenValue={typeof values[input.name] === "boolean" ? "" : values[input.name]} //for tools inputs keys conflicts
                        onTextChange={text => onChange(input.name, text)}
                        handleHelpFillField={desc => handleHelpFillField(desc, input.name)}
                        isMandatory={input.required}
                        isShowingMissingInputs={isShowingMissingInputs}
                    />
                );

            case "number":
                return (
                    <AINumberInput
                        id={`${tool}-${input.name}`}
                        key={`${input.name}-number`}
                        label={input.label}
                        unit={input.unit}
                        min={input.min}
                        max={input.max}
                        value={values[input.name]}
                        onChange={e => onChange(input.name, e.target.value)}
                        isMandatory={input.required}
                    />
                );

            case "textLength":
                return (
                    <TextLengthInput
                        id={`${tool}-${input.name}`}
                        key={`${input.name}-textLength`}
                        label={input.label}
                        inputProps={{
                            value: values[input.name],
                            onChange: e => onChange(input.name, e.target.value),
                        }}
                        selectProps={{
                            selected: values.textLengthUnit,
                            onChange: value => onChange("textLengthUnit", value),
                            options: TextLengthUnits,
                        }}
                        isHorizontalLayout={input.layout?.horizontal}
                        isMandatory={input.required}
                    />
                );

            case "boolean":
                return (
                    <AIBooleanInput
                        id={`${tool}-${input.name}`}
                        key={`${input.name}-textLength`}
                        label={input.label}
                        isMandatory={input.required}
                        enabled={values[input.name]}
                        onToggleChange={(isEnabled: boolean) => onChange(input.name, isEnabled)}
                    />
                );

            case "file":
                return (
                    <AIFilesDnDInput
                        id={`${tool}-${input.name}`}
                        key={`${input.name}-file`}
                        label={input.label}
                        droppedFile={droppedFile}
                        setDroppedFile={setDroppedFile}
                        isUploading={isUploadingFile}
                        stopUpload={handleStopUloadFile}
                    />
                );

            default:
                return null;
        }
    });
};

export default AiFormInputFactory;
